import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {Rounded} from "../../components/elements/rounded";
import { PageTitle } from '../../components/ui';
import Pagination from '../../components/elements/pagination/pagination';
import { DownFilled, LeftArrow, PencilIcon, TrashIcon } from '../../icons';
import Loader from '../../components/elements/loader';
import Container from '../../components/elements/container';
import { TableSkeletion } from '../../components/elements/skeletions';
import { CustomModal, TextField } from '../../components/ui';
import { PlusIcon, SearchIcon, DownIcon, UpArrow } from '../../icons';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '../../components/ui/Table';
import { NoDataFound } from '../../components/elements/noDataFound';
import { CompanyGroupForm } from '../../components/groupFleetAdmin/companyGroupForm';
import { useGetCompanyGroupsList, useDeleteGroup } from '../../hooks/react-query/useCompanyGroup';
import { Mapper } from '../../utils/user/mapper';
import { exportToFile, generatePdfDocument } from '../../utils/importDriver/funtions';
import { ExportList, ExportListMobile } from '../../components/elements/exportList';
import { exportOptions } from '../../utils/exports';
import { queryClient } from '../../utils/queryClient';
import { QUERY_KEYS } from '../../config/constants';
import { getCompanyGroupByIdFn } from '../../transactions/user/groupAdmin';
import { ConfirmationDialog } from '../../components/ui';
const { COMPANY_GROUPS, COMPANY_GROUPS_DETAILS } = QUERY_KEYS;

export const CompanyGroupsManagement = () => {

  const navigate = useNavigate();

  const [ openModal, setOpenModal ] = useState(false);
  const [ searchText, setSearchText ] = useState('');
  const [ popoverExport, setPopoverExport ] = useState(false);
  const [ popoverExportMobile, setPopoverExportMobile ] = useState(false);
  const [ groupListData, setGroupListData ] = useState();
  const [ filterState, setFilterState ] = useState({ length: 15, search: '', page: 1});
  const [ expandedRows, setExpandedRows ] = useState([])
  const [ membersMap, setMembersMap ] = useState(new Map());
  const [ groupSelectedForExpantionId, setGoupSelectedForExpantionId ] = useState();
  const [ modalMode, setModalMode ] = useState("create"); // "create" or "edit"
  const [ selectedGroup, setSelectedGroup ] = useState(null); // Group details for edit mode
  const [ deleteGroupOpenModal, setDeleteGroupOpenModal ] = useState(false);
  const [ groupToDelete, setGroupToDelete ] = useState(null); // Group to delete

  const { data: groups , isLoading: isGroupsFetching } = useGetCompanyGroupsList(filterState);
  const { mutate: deleteGroup, isLoading: isDeleteGroupLoading } = useDeleteGroup();

  useEffect(() => {
      if (groups && groups.pages && groups.pages[0].data) {
        setGroupListData(Mapper(groups.pages[0], 'companygroups'));
      }
    }, [groups]);

  useQuery({
    queryKey: [COMPANY_GROUPS_DETAILS,groupSelectedForExpantionId],
    queryFn: () => getCompanyGroupByIdFn(groupSelectedForExpantionId),
    enabled: !!groupSelectedForExpantionId, // Only fetch data if a group ID is selected
    refetchOnWindowFocus: true,
    onSuccess: (data) => {
      if(!!data && !!data.data && !!data.data.members ){
        setMembersMap((prevMap) => new Map(prevMap).set(groupSelectedForExpantionId, [...data.data.members]))
      }
    },
  });

  const handleToggleExpand = (companyGroup) => {
    
    setExpandedRows((prevExpandedRows) => {
      const isGroupExpanded = prevExpandedRows.includes(companyGroup.id);
      if (isGroupExpanded) {
        return prevExpandedRows.filter((id) => id !== companyGroup.id);
      } else {
        setGoupSelectedForExpantionId(companyGroup.id);
        return [...prevExpandedRows, companyGroup.id];
      }
    });
  }

  const isExpanded = (companyGroupId) => {
    return expandedRows.includes(companyGroupId);
  };

  const handleExport = (type) => {
    if (!groupListData || !groupListData.data || groupListData.data.length === 0) {
      return;
    }
    const flattenGroups = groupListData.data.map((group) => ({
      name: group.name,
      group_fleet_admin: group.group_fleet_admin.email, // Use the email of the fleet admin
    }))
    const flattenData = {data: flattenGroups, 
      columns: [
        {
          name: "name",
          label: "Group Name"
        },
        {
          name: "group_fleet_admin",
          label: "Group Fleet Admin"
        }
      ]}
    if (type === 'pdf') {
      generatePdfDocument(flattenData, 'Company Groups');
    } else {
      exportToFile(flattenData, type, 'CompanyGroups');
    }
  };

  const handleDeleteGroup = (group) => {
    deleteGroup(group.id, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: [COMPANY_GROUPS, filterState],
        });
        setDeleteGroupOpenModal(false)
      }
    })
  };

  const handleSortOrder = (item) => {
    let orderBy = item.name;
    let orderType = 'asc';
    if (item.name !== filterState?.orderBy) {
      orderBy = item.name;
    } else {
      orderType = filterState.orderType === 'asc' ? 'desc' : orderType;
    }

    setFilterState((prev) => ({ ...prev, orderType, orderBy, page: 1 }));
    
    // Sort the userListData locally
    setGroupListData((prev) => ({
      ...prev,
      data: [...prev.data].sort((a, b) => {
        if (orderType === 'asc') {
          return a[orderBy] > b[orderBy] ? 1 : -1;
        } else {
          return a[orderBy] < b[orderBy] ? 1 : -1;
        }
      }),
    }));
  };

  const handleSearch = (searchKey) => {
    setFilterState((prev) => ({ ...prev, search: searchKey, page: 1 }));
    setSearchText(searchKey); // Update the search text state
  };

  const debouncedHandleSearch = useMemo(() => {
    return debounce(handleSearch, 600);
  }, []);

  const handlePagination = (pageNum) => {
    setFilterState((prev) => ({ ...prev, page: pageNum }));
  };

  const handleOpenModal = (mode, group = null) => {
    setModalMode(mode); // Set the mode ("create" or "edit")
    setSelectedGroup(group); // Set the group details (null for create mode)
    setOpenModal(true); // Open the modal
  };
  
  if (isGroupsFetching && !groupListData) {
      return (
        <Container>
          <TableSkeletion />
        </Container>
      );
  }

  if (!groupListData || !groupListData.data) {
      return (
        <Container>
          <NoDataFound />
        </Container>
      );
  }

  const createHeaders = () => {
      let headers = [];
  
      headers.push(
        <TableHead key={"groupname"} scope="col">
          <button
            className="cursor-pointer flex"
            onClick={() => handleSortOrder("name")}
          >
            <h5 className="whitespace-nowrap text-body-large">
              Όνομα Group
            </h5>
            <span className="sort">
              <DownFilled className={clsx('fill-[#595454] h-6 w-6')} />
            </span>
          </button>
        </TableHead>
      );
      headers.push(
        <TableHead key={"groupfleetadmin"} scope="col">
          <button
            className="cursor-pointer flex"
            onClick={() => handleSortOrder("group_fleet_admin")}
          >
            <h5 className="whitespace-nowrap text-body-large">
              Group Fleet Admin
            </h5>
            <span className="sort">
              <DownFilled className={clsx('fill-[#595454] h-6 w-6')} />
            </span>
          </button>
        </TableHead>
      );
      headers.push(
        <TableHead key={"actions"} scope="col">
          <h5 className="whitespace-nowrap text-body-large">
            Ενέργειες
          </h5>
        </TableHead>
      );
      headers.push(
        <TableHead key={"members"} scope="col">
          <h5 className="whitespace-nowrap text-body-large">
            Μέλη
          </h5>
        </TableHead>
      );

      return (
        <TableHeader>
          <TableRow>{headers}</TableRow>
        </TableHeader>
      );
  };

  const createBody = () => {
    let rows = [];
    const { data } = groupListData;
    const dataArray = [...data];
  
    if (dataArray.length && !isGroupsFetching) {
      dataArray.forEach((item, index) => {
        const itm = { ...item };
        let cells = [];
  
        // Original row cells
        cells.push(
          <TableCell key={"groupname"} className={'text-body whitespace-nowrap'}>
            {!itm?.name ? "" : itm.name}
          </TableCell>
        );
  
        cells.push(
          <TableCell key={"groupfleetadmin"} className={'text-body whitespace-nowrap'}>
            {!itm?.group_fleet_admin?.email ? "" : 
              <a className="text-primary" href={`mailto:${itm.group_fleet_admin.email}`}>
                {itm.group_fleet_admin.email}
              </a>
            }
          </TableCell>
        );
  
        cells.push(
          <TableCell key={`${itm.id}-actionbuttons`} className={'text-body whitespace-nowrap'}>
            
            <div className="flex items-center gap-x-6">
              <CustomModal
                triggerClass={clsx('lg:max-h-[80vh] dialog-wrapper')}
                triggerComponent={
                  <>
                    <Rounded className={"border border-fadedgrey p-3 flex items-center justify-center"}> <TrashIcon width={22} height={25}/></Rounded>
                  </>
                }
                handleClick={(value) => {
                  setDeleteGroupOpenModal(value)
                  setGroupToDelete(itm);
                }}
                openModal={deleteGroupOpenModal}
                disabled={false}
              >
                <div className="container-sm mx-auto lg:py-10">
                  <ConfirmationDialog
                    message={`Είστε σίγουροι ότι θέλετε να διαγράψετε το Group ${groupToDelete?.name};`}
                    title="Διαγραφή Group"
                    handleOkClick= {()=> {
                      handleDeleteGroup(groupToDelete);
                    }}
                    isLoading={isDeleteGroupLoading}
                    handleCancelClick={() => setDeleteGroupOpenModal(false)}
                  />
                </div>
              </CustomModal>
              <Rounded className="border border-fadedgrey p-3 flex items-center justify-center">
                <button type="button" onClick={() => handleOpenModal("edit", itm)}>
                  <PencilIcon width={20} height={20} className="stroke-black" />
                </button>
              </Rounded>
            </div>
          </TableCell>
        );
  
        cells.push(
          <TableCell key={`${itm.id}-membersbutton`} className={'text-body whitespace-nowrap'}>
            <div className='flex w-full justify-center '>
              <button
                className="text-primary"
                onClick={() => handleToggleExpand(itm)}
              >
                {isExpanded(itm.id)
                  ? <UpArrow width='22' height='22' stroke='black' strokeWidth='2' />
                  : <DownIcon width={20} height={20} />
                }
              </button>
            </div>
          </TableCell>
        );
  
        rows.push(<TableRow key={itm.id}>{cells}</TableRow>);
  
        // Handle expanded rows
        if (isExpanded(itm.id)) {
          rows.push(
            <TableRow key={`${itm.id}-expandedtitlerow`}>
              <TableCell
                key={`${itm.id}-expandedtitlecell`}
                colSpan={4}
                className={'text-body whitespace-nowrap bg-powdergrey'}
              >
                <div className="flex flex-col gap-y-2 w-full items-end">
                  <h6 className="text-midgrey font-semibold pr-9">Στοιχεία Μελών</h6>
                </div>
              </TableCell>
            </TableRow>
          );

          if(membersMap.get(itm.id) && membersMap.get(itm.id).length > 0) {
            // Use map to generate expanded member rows for the specific group

            const memberRows = membersMap.get(itm.id).map((member, index) => (
              <TableRow key={`${itm.id}-${member.user.id}-${index}`}>
                <TableCell
                  key={`${index}-${itm.id}-${member.user.afm}-expanded`}
                  colSpan={4}
                  className={'text-body whitespace-nowrap bg-powdergrey'}
                  >
                  <div className="flex flex-col gap-y-2 w-full items-end">
                    <div className={clsx(`flex gap-x-4 w-full justify-end items-end`)}>
                      <p key={`${index}-${itm.id}-${member.user.company}-company`} className="text-sm pr-9">
                        {member.user.company}
                      </p>
                      <p key={`${index}-${itm.id}-${member.user.afm}-afm`} className="text-sm pr-9">
                        Α.Φ.Μ.: {member.user.afm}
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ));
          
            // Concatenate the generated member rows to the main rows array
            rows = rows.concat(memberRows || []);
          } else if (membersMap.get(itm.id) && membersMap.get(itm.id).length === 0){
            rows.push(
              <TableRow key={`${itm.id}-expandednodatarow`}>
                <TableCell
                  key={`${itm.id}-expandednodatacell`}
                  colSpan={4}
                  className={'text-body whitespace-nowrap bg-powdergrey'}
                >
                  <div className="flex flex-col gap-y-2 w-full items-end">
                    <p className="text-midgrey text-sm pr-9">(Δεν υπάρχουν Μέλη)</p>
                  </div>
                </TableCell>
              </TableRow>
            );
          }
        }
      });
  
      return <TableBody>{rows}</TableBody>;
    }
  
    return (
      <TableBody>
        <TableRow>
          <TableCell
            className="text-body whitespace-nowrap text-center"
            colSpan="10"
          >
            Κανένα Αποτέλεσμα
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  return (
      <>
        {isGroupsFetching && groupListData.data && <Loader />}
        <CustomModal
          triggerComponent={null} // No trigger component since the modal is opened programmatically
          wrapperClass="grid h-[calc(100vh-15rem)] overflow-y-auto scrollbar"
          handleClick={(value) => setOpenModal(value)}
          openModal={openModal}
          >
            <div className="container-sm lg:mx-auto flex flex-col items-center lg:gap-y-5 max-lg:px-5">
              <CompanyGroupForm
                mode={modalMode} // Pass the mode ("create" or "edit")
                group={selectedGroup} // Pass the group details (null for create mode)
                handleClose={() => {
                  setOpenModal(false)
                  queryClient.invalidateQueries({
                    queryKey: [COMPANY_GROUPS_DETAILS,selectedGroup.id]
                  })
                }} // Close the modal
                managementFilterState={filterState} // Pass the filter state in order to invalidate the current page query
              />
            </div>
        </CustomModal>
        <Container className="flex flex-col gap-6 max-h-[88vh] max-lg:px-0">
            {isDeleteGroupLoading && <Loader className="fill-primary" />}
            <PageTitle
                displaySearch={false}
                wrapperClassName="flex-col max-lg:px-5"
                title={
                    <div className="flex max-lg:gap-x-8 items-center">
                        <button className='lg:p-2' onClick={() => navigate('/dashboard')}>
                          <LeftArrow width={32} height={32} />
                        </button>
                        <h2>Company Groups Management</h2>
                    </div>
                }
                endComponent={
                  <div>
                      <div className="flex lg:flex-cols-3 gap-5 max-lg:gap-4 pb-5">
                      <TextField
                          wrapperClass={clsx('flex-1', "searchClassName")}
                          className={
                              'h-full w-80 rounded-full text-body border-none items-center px-5 lg:py-4 py-3'
                          }
                          placeholder={'Αναζήτηση'}
                          value={searchText}
                          onChange={(e) => {
                            setSearchText(e.target.value);
                            debouncedHandleSearch(e.target.value);
                          }}
                          icon={<SearchIcon />}
                      />
                      <ExportList
                        isOpen={popoverExport}
                        exportOptions={exportOptions}
                        setPopoverExport={setPopoverExport}
                        handleExport={(value) => {
                          handleExport(value);
                          setPopoverExport(false);
                        }}
                      />
                      <ExportListMobile
                        isOpen={popoverExportMobile}
                        exportOptions={exportOptions}
                        setPopoverExport={setPopoverExportMobile}
                        handleExport={handleExport}
                      />
                      <button
                          type="button"
                          onClick={() => handleOpenModal("create")}
                          className="flex items-center gap-3"
                        >
                          <span className="max-lg:hidden text-primary-button">Προσθήκη Company Group</span>
                          <PlusIcon className="w-8 h-8" />
                      </button>
                    </div>
                  </div>
                }
            />
            <Table wrapperClassName="lg:p-4 p-2 scrollbar overflow-auto max-lg:rounded-none max-lg:min-h-[250px]">
                {createHeaders()}
                {createBody()}
            </Table>
            <Pagination
              currentPage={groupListData?.pagination?.current_page}
              totalCount={groupListData?.records?.total}
              from={groupListData?.records.from}
              to={groupListData?.records.to}
              pageSize={filterState.length}
              onPageChange={handlePagination}
            />
        </Container>
      </>
  );
};